import { TaskBase } from "../services/taskManager.js";

export class CultivateTask extends TaskBase {
  constructor() {
    super({
      id: "cultivate",
      name: "培养武将",
      description: "自动培养武将属性，根据策略保存或放弃培养结果",
      icon: "⚔️",
      category: "character",
      defaultConfig: {
        generalId: "",
        mode: "1", // 1=银币, 2=元宝
        times: 10,
        target: "balance", // balance=平衡, wuli=武力, zhili=智力, tili=体力
        requestInterval: 300,
      },
    });

    // 武将列表缓存
    this.generalListCache = {};
    this.cacheExpireTime = 5 * 60 * 1000; // 5分钟缓存过期时间
  }

  async getUIConfig() {
    const config = this.defaultConfig;
    return {
      controls: [
        {
          type: "select",
          id: "GeneralSelect",
          key: "generalId",
          label: "选择武将",
          options: [
            { value: "", text: "请先刷新武将列表", selected: true },
          ],
        },
        {
          type: "select",
          id: "ModeSelect",
          key: "mode",
          label: "培养方式",
          options: [
            { value: "1", text: "银币培养", selected: config.mode === "1" },
            { value: "2", text: "元宝培养", selected: config.mode === "2" },
          ],
        },
        {
          type: "number",
          id: "TimesInput",
          key: "times",
          label: "培养次数",
          min: 1,
          max: 999,
          default: config.times || 10,
        },
        {
          type: "select",
          id: "TargetSelect",
          key: "target",
          label: "培养目标",
          options: [
            { value: "balance", text: "平衡发展", selected: config.target === "balance" },
            { value: "wuli", text: "主练武力", selected: config.target === "wuli" },
            { value: "zhili", text: "主练智力", selected: config.target === "zhili" },
            { value: "tili", text: "主练体力", selected: config.target === "tili" },
          ],
        },
        {
          type: "number",
          id: "RequestIntervalInput",
          key: "requestInterval",
          label: "请求间隔（毫秒）",
          min: 0,
          max: 10000,
          default: config.requestInterval ?? 300,
        },
      ],
    };
  }

  // 获取培养信息
  async getCultivateInfo(accountKey) {
    const result = await this.simpleApiRequest(
      accountKey,
      "cultivate",
      "index"
    );
    return result;
  }

  // 执行培养roll
  async cultivateRoll(accountKey, generalId, mode) {
    const result = await this.simpleApiRequest(
      accountKey,
      "cultivate",
      "roll",
      { gid: generalId, mode: mode }
    );
    return result;
  }

  // 保存培养结果
  async saveCultivate(accountKey, generalId) {
    const result = await this.simpleApiRequest(
      accountKey,
      "cultivate",
      "save",
      { gid: generalId }
    );
    return result;
  }

  // 放弃培养结果
  async giveupCultivate(accountKey, generalId) {
    const result = await this.simpleApiRequest(
      accountKey,
      "cultivate",
      "giveup",
      { gid: generalId }
    );
    return result;
  }

  // 获取武将列表
  async getGeneralList(accountKey, indexData = null) {
    // 检查缓存
    const cacheKey = accountKey;
    const now = Date.now();
    if (
      this.generalListCache[cacheKey] &&
      now - this.generalListCache[cacheKey].timestamp < this.cacheExpireTime
    ) {
      return this.generalListCache[cacheKey].data;
    }

    let data = indexData;
    if (!data) {
      data = await this.getCultivateInfo(accountKey);
    }

    if (data && data.status === 1 && data.list) {
      const generalList = Object.values(data.list);
      // 更新缓存
      this.generalListCache[cacheKey] = {
        data: generalList,
        timestamp: now,
      };
      return generalList;
    }
    return [];
  }

  // 单次培养逻辑
  async cultivateOnce(accountKey, generalId, mode, target) {
    try {
      // 获取当前属性
      const infoData = await this.getCultivateInfo(accountKey);
      let general = null;
      if (infoData && infoData.status === 1 && infoData.list) {
        general = Object.values(infoData.list).find(
          (g) => String(g.id) === String(generalId)
        );
      }

      if (!general) {
        this.addLog(`[${accountKey}] 获取武将信息失败`, "error");
        return { saved: false };
      }

      const current = {
        wuliup: parseInt(general.wuliup) || 0,
        zhiliup: parseInt(general.zhiliup) || 0,
        tiliup: parseInt(general.tiliup) || 0,
      };

      // 执行roll
      const rollData = await this.cultivateRoll(accountKey, generalId, mode);
      if (!rollData || rollData.status !== 1 || !rollData.info) {
        this.addLog(`[${accountKey}] 培养roll失败`, "error");
        return { saved: false };
      }

      const { wuliup, zhiliup, tiliup } = rollData.info;
      const newAttr = {
        wuliup: parseInt(wuliup) || 0,
        zhiliup: parseInt(zhiliup) || 0,
        tiliup: parseInt(tiliup) || 0,
      };

      // 判定保存/放弃
      let shouldSave = false;
      if (target === "balance") {
        const dw = newAttr.wuliup - current.wuliup;
        const dz = newAttr.zhiliup - current.zhiliup;
        const dt = newAttr.tiliup - current.tiliup;
        const sum = dw + dz + dt;

        if (sum > 0) {
          shouldSave = true;
        } else if (sum === 0) {
          const baseArr = [
            { key: "wuliup", val: current.wuliup, delta: dw },
            { key: "zhiliup", val: current.zhiliup, delta: dz },
            { key: "tiliup", val: current.tiliup, delta: dt },
          ];
          baseArr.sort((a, b) => a.val - b.val);
          if (baseArr[0].delta > 0) {
            shouldSave = true;
          }
        }
      } else if (target === "wuli") {
        shouldSave = newAttr.wuliup > current.wuliup;
      } else if (target === "zhili") {
        shouldSave = newAttr.zhiliup > current.zhiliup;
      } else if (target === "tili") {
        shouldSave = newAttr.tiliup > current.tiliup;
      }

      // 计算属性变化
      const delta = {
        wuli: newAttr.wuliup - current.wuliup,
        zhili: newAttr.zhiliup - current.zhiliup,
        tili: newAttr.tiliup - current.tiliup,
        total:
          newAttr.wuliup +
          newAttr.zhiliup +
          newAttr.tiliup -
          (current.wuliup + current.zhiliup + current.tiliup),
      };

      const deltaStr = `武${delta.wuli >= 0 ? "+" : ""}${delta.wuli} 智${
        delta.zhili >= 0 ? "+" : ""
      }${delta.zhili} 体${delta.tili >= 0 ? "+" : ""}${delta.tili} 总${
        delta.total >= 0 ? "+" : ""
      }${delta.total}`;

      if (shouldSave) {
        const saveData = await this.saveCultivate(accountKey, generalId);
        this.addLog(
          `[${accountKey}] 培养保存: ${
            saveData && saveData.status === 1 ? "成功" : "失败"
          }，${deltaStr}`,
          saveData && saveData.status === 1 ? "success" : "error"
        );
        return { saved: saveData && saveData.status === 1, newAttr };
      } else {
        const giveupData = await this.giveupCultivate(accountKey, generalId);
        this.addLog(
          `[${accountKey}] 培养放弃: ${
            giveupData && giveupData.status === 1 ? "成功" : "失败"
          }，${deltaStr}`,
          giveupData && giveupData.status === 1 ? "info" : "error"
        );
        return { saved: false, newAttr };
      }
    } catch (error) {
      this.addLog(
        `[${accountKey}] 培养失败: ${error.message}`,
        "error"
      );
      return { saved: false };
    }
  }

  async main(accountKey) {
    this.addLog(`[${accountKey}] 开始自动培养武将...`, "info");

    const config = this.getAccountConfig(accountKey);
    let { generalId, mode, times, target } = config;
    const requestInterval = Number(config.requestInterval ?? 300);
    if (Number.isFinite(requestInterval) && requestInterval >= 0 && this.appStore?.apiService) {
      this.appStore.apiService.requestInterval = Math.floor(requestInterval);
    }

    if (!generalId || !mode || !times || !target) {
      this.addLog(
        `[${accountKey}] 请完整选择培养参数`,
        "error"
      );
      throw new Error("请完整选择培养参数");
    }

    try {
      // 获取武将初始属性和消耗规则
      const indexData = await this.getCultivateInfo(accountKey);
      if (!indexData || indexData.status !== 1) {
        throw new Error("获取培养信息失败");
      }

      const generals = await this.getGeneralList(accountKey, indexData);
      const selectedGeneral = generals.find(
        (g) => String(g.id) === String(generalId)
      );

      if (!selectedGeneral) {
        throw new Error("未找到指定武将");
      }

      let initialAttr = {
        wuliup: parseInt(selectedGeneral.wuliup) || 0,
        zhiliup: parseInt(selectedGeneral.zhiliup) || 0,
        tiliup: parseInt(selectedGeneral.tiliup) || 0,
      };

      let lastSavedAttr = { ...initialAttr };
      let successCount = 0;

      this.addLog(
        `[${accountKey}] 开始培养武将: ${selectedGeneral.name}，初始属性 武${initialAttr.wuliup} 智${initialAttr.zhiliup} 体${initialAttr.tiliup}`,
        "info"
      );

      // 批量培养
      for (let i = 1; i <= times; i++) {
        this.addLog(`[${accountKey}] 第${i}/${times}次培养...`, "info");

        const result = await this.cultivateOnce(accountKey, generalId, mode, target);

        if (result && result.saved) {
          successCount++;
          if (result.newAttr) {
            lastSavedAttr = { ...result.newAttr };
          }
        }
      }

      // 计算属性变化
      const delta = {
        wuli: lastSavedAttr.wuliup - initialAttr.wuliup,
        zhili: lastSavedAttr.zhiliup - initialAttr.zhiliup,
        tili: lastSavedAttr.tiliup - initialAttr.tiliup,
        total:
          lastSavedAttr.wuliup +
          lastSavedAttr.zhiliup +
          lastSavedAttr.tiliup -
          (initialAttr.wuliup + initialAttr.zhiliup + initialAttr.tiliup),
      };

      const summary = `培养完成，成功${successCount}/${times}次，属性变化：武${
        delta.wuli >= 0 ? "+" : ""
      }${delta.wuli} 智${delta.zhili >= 0 ? "+" : ""}${delta.zhili} 体${
        delta.tili >= 0 ? "+" : ""
      }${delta.tili} 总${delta.total >= 0 ? "+" : ""}${delta.total}`;

      this.addLog(`[${accountKey}] ${summary}`, "success");
      return true;
    } catch (error) {
      this.addLog(
        `[${accountKey}] 自动培养武将失败: ${error.message}`,
        "error"
      );
      throw error;
    }
  }
}
